import { createSupabaseServerClient } from "@/lib/api";
import type { RankListType } from "../types";
import { RANK_LIST_CONFIGS } from "../types";

export type RankHistoryPoint = {
  timestamp: number;
  rank: number | null;
};

type HistoryRow = {
  ts: number;
  rank?: number | null;
  followers?: number | null;
};

const HISTORY_BATCH_SIZE = 1000;
const HISTORY_MAX_ROWS = 20000;
const CLIENT_RETRY_MAX_ATTEMPTS = 2;
const CLIENT_RETRY_BASE_DELAY_MS = 350;

// Server-side downsampling: для діапазонів > 7д агрегуємо дані в бакети
// через RPC (MIN(rank) для ранкових, MAX(value) для followers/mlt) замість
// того щоб тягнути всі 10-хв точки. Це і швидше для БД, і легше для чарту.
const HOUR = 60 * 60;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const QUARTER = 90 * DAY;
const YEAR = 365 * DAY;

/**
 * Підбираємо розмір бакета щоб точок на чарті було ~300-1000.
 * Експортована — клієнт використовує її як ключ для in-memory chart cache,
 * щоб не повертати рідкі (бакетовані) дані для дрібнішого діапазону.
 */
export function pickBucketSeconds(rangeSeconds: number): number {
  if (rangeSeconds <= WEEK) return 0; // raw
  if (rangeSeconds <= MONTH) return HOUR; // 30d → ~720 точок
  if (rangeSeconds <= QUARTER) return 6 * HOUR; // 3m → ~360 точок
  if (rangeSeconds <= YEAR) return DAY; // 1y → ~365 точок
  return DAY; // all time → 1 точка/день
}

/**
 * Вікно округлення `from`/`to` для CDN-кешування. Без цього timeAnchor=Date.now()
 * робить унікальний URL на кожен page-load, тож CDN не шарить між юзерами і
 * кожен hit йде в БД (де cold-cache 1р = 4-5с). Округлення робить URL однаковим
 * у межах вікна свіжості — далі CDN віддає готову відповідь за ~50мс.
 */
export function freshnessWindowSec(bucketSeconds: number): number {
  // raw (дефолтний ≤7d діапазон!) — 5хв вікно. Старе значення 1с означало
  // «без округлення»: Date.now()-якір робив URL унікальним на кожен page-load,
  // CDN hit ≈ 0 навіть для однієї популярної гри — КОЖЕН перегляд чарта бив у
  // БД. Дані оновлюються воркером раз на 10хв, тож 5хв вікно жертвує максимум
  // пів-кадансу свіжості за 10-50× менше запитів у БД.
  if (bucketSeconds === 0) return 5 * 60;
  if (bucketSeconds <= HOUR) return 5 * 60; // 1h-бакети → ~5хв свіжість
  if (bucketSeconds <= 6 * HOUR) return 30 * 60; // 6h-бакети → ~30хв свіжість
  return HOUR; // 1d-бакети → ~1 година свіжість
}

function sleep(ms: number, signal?: AbortSignal | null) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }

    const id = setTimeout(() => resolve(), ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(id);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });
}

function isStatementTimeoutMessage(message: string | undefined): boolean {
  if (!message) return false;
  return (
    message.toLowerCase().includes("statement timeout") ||
    message
      .toLowerCase()
      .includes("canceling statement due to statement timeout")
  );
}

type GetRankHistoryServerParams = {
  type: RankListType;
  appId: number;
  from: number;
  to: number;
  /**
   * Optional region code (cc). Used only for rank lists that support regions
   * (currently: topsellers). Use "global" or omit for global.
   */
  cc?: string;
  /**
   * Bucket size to read at instead of the one the window's width picks. Only
   * the API sets it, to read on past the database's row limit at the bucket
   * size of the window it started from.
   */
  bucketSeconds?: number;
};

/**
 * Server function to fetch rank history directly from Supabase.
 * Used by API Routes and Server Components.
 */
export async function getRankHistoryServer({
  type,
  appId,
  from,
  to,
  cc,
  bucketSeconds: bucketOverride,
}: GetRankHistoryServerParams): Promise<RankHistoryPoint[]> {
  if (type === "publishers") {
    throw new Error("Rank history is not supported for publishers");
  }

  if (!Number.isFinite(appId) || appId <= 0) {
    throw new Error("Valid numeric appId is required");
  }

  if (!Number.isFinite(to) || to <= 0) {
    throw new Error("`to` parameter must be a valid positive timestamp");
  }

  if (from !== 0 && (!Number.isFinite(from) || from > to)) {
    throw new Error("`from` and `to` parameters must be valid timestamps");
  }

  const config = RANK_LIST_CONFIGS[type];
  if (!config) {
    throw new Error(`Invalid rank list type: ${type}`);
  }
  if (!config.historyTableName) {
    throw new Error(`Rank history table is not configured for type: ${type}`);
  }
  const historyTableName = config.historyTableName;

  const supabase = await createSupabaseServerClient();
  const isTopSellers = type === "topsellers";
  const isNewTrending = type === "newtrending";
  // New & Trending has no global list — default/normalise to "us".
  const rawCc = (cc ?? (isNewTrending ? "us" : "global")).toLowerCase();
  const effectiveCc =
    isNewTrending && (rawCc === "" || rawCc === "global") ? "us" : rawCc;
  const isRegion = (isTopSellers && effectiveCc !== "global") || isNewTrending;

  // Для довгих діапазонів — агрегуємо в бакети на сервері через RPC.
  // Це дає 300-1000 точок замість десятків тисяч і кешується CDN.
  const effectiveFrom = from > 0 ? from : 0;
  const rangeSeconds =
    from > 0 ? Math.max(0, to - effectiveFrom) : Number.MAX_SAFE_INTEGER;
  const bucketSeconds =
    bucketOverride !== undefined && bucketOverride > 0
      ? bucketOverride
      : pickBucketSeconds(rangeSeconds);

  if (bucketSeconds > 0) {
    type BucketRow = { ts: number; value: number | null };
    // New & Trending uses a dedicated RPC (keeps the shared multiplexer untouched).
    const { data, error } = isNewTrending
      ? await supabase.schema("analytics").rpc("get_newtrending_history_bucketed", {
          p_appid: appId,
          p_from: Math.floor(effectiveFrom),
          p_to: Math.floor(to),
          p_bucket_seconds: bucketSeconds,
          p_cc: effectiveCc,
        })
      : await supabase.schema("analytics").rpc("get_rank_history_bucketed", {
          p_type: type,
          p_appid: appId,
          p_from: Math.floor(effectiveFrom),
          p_to: Math.floor(to),
          p_bucket_seconds: bucketSeconds,
          p_cc: isRegion ? effectiveCc : null,
        });

    if (error) {
      console.error(`[${type} history bucketed] Supabase error:`, error);
      throw new Error(
        `Failed to fetch ${config.displayName.toLowerCase()} history: ${error.message}`,
      );
    }

    const rowsBucketed = (data as BucketRow[] | null) ?? [];
    return rowsBucketed.map((row) => ({
      timestamp: Number(row.ts),
      rank: row.value == null ? null : Number(row.value),
    }));
  }

  const buildBaseQuery = (afterTs?: number) => {
    // followers uses "followers", other lists use "rank"
    const selectFields = type === "followers" ? "ts, followers" : "ts, rank";
    const tableName = isNewTrending
      ? "steam_newtrending_history_region"
      : isRegion
        ? "steam_topsellers_history_region"
        : historyTableName;
    let baseQuery = supabase
      .schema("analytics")
      .from(tableName)
      .select(selectFields)
      .eq("appid", appId);

    if (isRegion) {
      baseQuery = baseQuery.eq("cc", effectiveCc);
    }

    if (from > 0) {
      baseQuery = baseQuery.gte("ts", Math.floor(from));
    }

    if (afterTs != null) {
      // Keyset pagination (avoids OFFSET, usually much faster on big tables)
      baseQuery = baseQuery.gt("ts", afterTs);
    }

    return baseQuery.lte("ts", Math.floor(to)).order("ts", { ascending: true });
  };

  const rows: HistoryRow[] = [];
  let lastTs: number | undefined;

  while (rows.length < HISTORY_MAX_ROWS) {
    const { data, error } = await buildBaseQuery(lastTs).range(
      0,
      HISTORY_BATCH_SIZE - 1
    );

    if (error) {
      console.error(`[${type} history] Supabase error:`, error);
      throw new Error(
        `Failed to fetch ${config.displayName.toLowerCase()} history: ${
          error.message
        }`
      );
    }

    if (!data || data.length === 0) {
      break;
    }

    rows.push(...data);
    lastTs = data[data.length - 1]?.ts;

    if (data.length < HISTORY_BATCH_SIZE) {
      break;
    }
  }

  if (rows.length === HISTORY_MAX_ROWS) {
    console.warn(
      `[${type} history] Reached max rows (${HISTORY_MAX_ROWS}) for app ${appId}`
    );
  }

  return rows.map((row: HistoryRow) => ({
    timestamp: row.ts,
    rank: type === "followers" ? row.followers ?? null : row.rank ?? null,
  }));
}

type GetRankHistoryParams = {
  type: RankListType;
  appId: number;
  from: number;
  to: number;
  cc?: string;
  init?: RequestInit;
};

/**
 * Client function to fetch rank history via API Route.
 * Used by React hooks (Client Components).
 */
export async function getRankHistory({
  type,
  appId,
  from,
  to,
  cc,
  init,
}: GetRankHistoryParams): Promise<RankHistoryPoint[]> {
  if (!Number.isFinite(appId)) {
    throw new Error("appId is required");
  }

  // Округлюємо from/to до межі вікна свіжості → URL стабільний між юзерами
  // в одному вікні → CDN віддає готову відповідь без походу в БД.
  // Для raw (≤7д) — без округлення, бо дані оновлюються кожні 10хв.
  const rangeSeconds =
    from > 0 ? Math.max(0, to - from) : Number.MAX_SAFE_INTEGER;
  const bucketSeconds = pickBucketSeconds(rangeSeconds);
  const window = freshnessWindowSec(bucketSeconds);
  const roundedTo = window > 1 ? Math.floor(to / window) * window : to;
  const roundedFrom =
    from > 0 && window > 1 ? Math.floor(from / window) * window : from;

  const searchParams = new URLSearchParams({
    from: Math.floor(roundedFrom).toString(),
    to: Math.floor(roundedTo).toString(),
  });

  if (cc && cc !== "global") {
    searchParams.set("cc", cc);
  }

  const url = `/api/games/${appId}/rank-history/${type}?${searchParams.toString()}`;

  for (let attempt = 0; attempt <= CLIENT_RETRY_MAX_ATTEMPTS; attempt++) {
    const response = await fetch(url, {
      cache: "no-store",
      ...init,
    });

    let payload: { history?: RankHistoryPoint[]; error?: string } | null = null;
    try {
      payload = (await response.json()) as {
        history?: RankHistoryPoint[];
        error?: string;
      };
    } catch {
      payload = null;
    }

    if (response.ok) {
      return payload?.history ?? [];
    }

    const config = RANK_LIST_CONFIGS[type];
    const message =
      payload?.error ??
      `Failed to fetch ${config.displayName.toLowerCase()} history`;

    const isRetryable =
      response.status >= 500 && isStatementTimeoutMessage(message);

    if (!isRetryable || attempt === CLIENT_RETRY_MAX_ATTEMPTS) {
      throw new Error(message);
    }

    // Exponential-ish backoff with small jitter
    const jitter = Math.floor(Math.random() * 120);
    const delay = CLIENT_RETRY_BASE_DELAY_MS * (attempt + 1) + jitter;
    await sleep(delay, init?.signal);
  }

  // Unreachable, but TS-friendly
  throw new Error("Failed to fetch history");
}
